import { Show } from "@clerk/react"
import { createFileRoute, Outlet, useLocation, useSearch } from "@tanstack/react-router"
import { useState } from "react"
import { Plus, BrainCircuit } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { FullCaptureUI } from "@/components/capture-form"
import { useDocumentStats } from "@mimir/api-client"
import { GridBackground } from "@/components/backgrounds/grid-bg"
import { PageHeader } from "@/components/page-header"
import { CreateRoomDialog } from "@/components/mind-palace/create-room-dialog"

export const Route = createFileRoute("/_app/mind-palace")({
  component: MindPalaceLayout,
})

function MindPalaceLayout() {
  const [isCaptureOpen, setIsCaptureOpen] = useState(false)
  const location = useLocation()
  const { data: stats } = useDocumentStats()
  const atMindPlaceIndex =
    location.pathname === "/mind-palace" || location.pathname === "/mind-palace/"
  const atRoomPage = location.pathname.startsWith("/mind-palace/room/")
  const search = useSearch({ strict: false }) as unknown as { layout: string }
  const isRoomsLayout = search.layout === "rooms"

  return (
    <>
      <Show when="signed-out">
        <p className="text-sm text-muted-foreground mx-12">Sign in to view your Mind Palace.</p>
      </Show>
      <Show when="signed-in">
        <GridBackground vignette={true} className="h-screen opacity-25! z-[-1] absolute" />
        <div className="flex flex-col gap-6 h-full w-[85%] min-w-[800px] mx-auto pt-10 pb-4">
          {atMindPlaceIndex && !atRoomPage && (
            <PageHeader
              icon={BrainCircuit}
              title="Mind Palace"
              subtitle={
                stats && stats.total > 0
                  ? `${stats.total} things trusted to memory. Browse, or let Mimir walk you through what's connected.`
                  : "Browse, or let Mimir walk you through what's connected."
              }
            >
              <div className="flex gap-3 items-center shrink-0">
                {isRoomsLayout && (
                  <CreateRoomDialog
                    trigger={
                      <Button className="px-6" variant="outline">
                        <Plus className="h-4 w-4 mr-1" />
                        Create Room
                      </Button>
                    }
                  />
                )}
                <Dialog open={isCaptureOpen} onOpenChange={setIsCaptureOpen}>
                  <DialogTrigger
                    nativeButton={true}
                    render={
                      <Button className="px-6">
                        <Plus className="h-4 w-4" />
                        New Document
                      </Button>
                    }
                  />
                  <DialogContent
                    className="sm:w-[95vw] sm:max-w-5xl h-[88vh] p-0 overflow-hidden border-0 bg-transparent shadow-none"
                    showCloseButton={false}
                  >
                    <FullCaptureUI onClose={() => setIsCaptureOpen(false)} />
                  </DialogContent>
                </Dialog>
              </div>
            </PageHeader>
          )}
          <div className="flex-1 min-h-0 overflow-hidden">
            <Outlet />
          </div>
        </div>
      </Show>
    </>
  )
}
